import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import BanquetHelpPanel from './BanquetHelpPanel';
import useIsMobile from '../hooks/useIsMobile';

const emptyItem = () => ({ item_name: '', description: '', qty: '1', unit_price: '', line_amount: '' });

function BanquetBooking({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const { isMobile } = useIsMobile();
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [bookings, setBookings] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [quotationItems, setQuotationItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_date: '', gstin_no: '', invoice_amount: '', tax_id: '', items: [emptyItem()] });
  const [adjustAmount, setAdjustAmount] = useState('');
  const [invoiceItemsById, setInvoiceItemsById] = useState({});
  const [openInvoiceItemsId, setOpenInvoiceItemsId] = useState('');

  // Advance payment form
  const [advanceForm, setAdvanceForm] = useState({ amount: '', payment_mode: 'CASH', gateway_ref: '', receipt_no: '' });
  const [advanceBusy, setAdvanceBusy] = useState(false);

  // Active section
  const [activeSection, setActiveSection] = useState('advance'); // advance | invoice | history

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadBookings = async () => {
    const res = await fetch('/api/v1/banquet/bookings', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load bookings');
    const list = Array.isArray(data) ? data : [];
    setBookings(list);
    if (!selectedBookingId && list.length) setSelectedBookingId(String(list[0].booking_id));
  };

  const loadInvoices = async (bookingId) => {
    if (!bookingId) return setInvoices([]);
    const res = await fetch(`/api/v1/banquet/bookings/${bookingId}/invoices`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load invoices');
    setInvoices(Array.isArray(data) ? data : []);
  };

  const loadQuotationItems = async (bookingId) => {
    if (!bookingId) return setQuotationItems([]);
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${bookingId}/quotation-items`, { headers });
      const { data } = await parseJson(res);
      setQuotationItems(Array.isArray(data) ? data : []);
    } catch { setQuotationItems([]); }
  };

  const loadPayments = async (bookingId) => {
    if (!bookingId) return setPayments([]);
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${bookingId}/payments`, { headers });
      const data = await res.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch { setPayments([]); }
  };

  const loadTaxOptions = async () => {
    const res = await fetch('/api/v1/banquet/tax-options', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load tax options');
    setTaxOptions(Array.isArray(data) ? data : []);
  };

  const loadInvoiceItems = async (invoiceId) => {
    if (!invoiceId) return;
    const res = await fetch(`/api/v1/banquet/invoices/${invoiceId}/items`, { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load invoice items');
    setInvoiceItemsById((prev) => ({ ...prev, [invoiceId]: Array.isArray(data) ? data : [] }));
  };

  const reload = async () => {
    if (!chainId || !propertyId) return;
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadBookings(), loadTaxOptions()]);
    } catch (err) {
      setError(err?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [chainId, propertyId, outletId]);

  useEffect(() => {
    if (!selectedBookingId) return;
    loadInvoices(selectedBookingId).catch(() => {});
    loadQuotationItems(selectedBookingId);
    loadPayments(selectedBookingId);
  }, [selectedBookingId]);

  // Auto-select tax + determine default section based on booking status
  useEffect(() => {
    const bk = bookings.find((b) => String(b.booking_id) === String(selectedBookingId));
    if (!bk || !taxOptions.length) return;
    const qTax = Number(bk.quotation_tax_percent || 0);
    if (qTax > 0) {
      const match = taxOptions.find((t) => Math.abs(Number(t.tax_percent || 0) - qTax) < 0.01);
      if (match) setInvoiceForm((prev) => ({ ...prev, tax_id: String(match.id) }));
    }
    // Set default section based on status
    const status = String(bk.booking_status || '').toUpperCase();
    if (status === 'TENTATIVE') setActiveSection('advance');
    else if (status === 'CONFIRMED') setActiveSection('advance');
    else if (status === 'COMPLETED') setActiveSection('invoice');
    else setActiveSection('history');
  }, [selectedBookingId, bookings, taxOptions]);

  const selectedBooking = useMemo(
    () => bookings.find((b) => String(b.booking_id) === String(selectedBookingId)) || null,
    [bookings, selectedBookingId]
  );

  // ── Copy quotation items to invoice form ──
  const copyQuotationToInvoice = useCallback(() => {
    if (!quotationItems.length) return;
    const bk = selectedBooking;
    const items = quotationItems.map((qi) => ({
      item_name: qi.description || qi.item_type || 'Banquet Service',
      description: qi.item_type ? `${qi.item_type}` : '',
      qty: '1',
      unit_price: String(Number(qi.amount || 0).toFixed(2)),
      line_amount: String(Number(qi.amount || 0).toFixed(2))
    }));
    const today = new Date().toISOString().slice(0, 10);
    setInvoiceForm((prev) => ({
      ...prev,
      items,
      invoice_date: prev.invoice_date || today
    }));
    setMessage('Quotation items loaded into invoice. Review and click "Post Invoice".');
  }, [quotationItems, selectedBooking]);

  // ── Create Invoice ──
  const createInvoice = async () => {
    setError('');
    setMessage('');
    if (!selectedBookingId) return setError('Select booking first');
    const validItems = (invoiceForm.items || [])
      .map((it) => ({ ...it, item_name: String(it.item_name || '').trim() }))
      .filter((it) => it.item_name);
    if (!validItems.length) return setError('Add at least one invoice item');
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${selectedBookingId}/invoice`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_date: invoiceForm.invoice_date || null,
          gstin_no: invoiceForm.gstin_no || null,
          invoice_amount: Number(invoiceBase),
          tax_id: invoiceForm.tax_id ? Number(invoiceForm.tax_id) : null,
          items: validItems.map((row) => {
            const qty = Number(row.qty || 1);
            const unitPrice = Number(row.unit_price || 0);
            const lineAmount = Number(row.line_amount || (qty * unitPrice));
            return {
              item_name: row.item_name,
              description: row.description || null,
              qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
              unit_price: Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice : 0,
              line_amount: Number.isFinite(lineAmount) && lineAmount >= 0 ? lineAmount : 0
            };
          })
        })
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to create invoice');
      setMessage(`Invoice posted: ${data?.invoice_no || data?.invoice_id}`);
      setInvoiceForm({ invoice_date: '', gstin_no: '', invoice_amount: '', tax_id: '', items: [emptyItem()] });
      setOpenInvoiceItemsId('');
      await Promise.all([loadBookings(), loadInvoices(selectedBookingId)]);
    } catch (err) {
      setError(err?.message || 'Failed to post invoice');
    }
  };

  // ── Record Advance Payment ──
  const recordAdvance = async () => {
    setError('');
    setMessage('');
    if (!selectedBookingId) return setError('Select booking first');
    const amount = Number(advanceForm.amount || 0);
    if (amount <= 0) return setError('Enter a valid advance amount');
    setAdvanceBusy(true);
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${selectedBookingId}/payments`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          payment_type: 'ADVANCE',
          payment_mode: advanceForm.payment_mode || 'CASH',
          gateway_ref: advanceForm.gateway_ref || '',
          receipt_no: advanceForm.receipt_no || ''
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to record payment');
      const paidNow = Number(data?.totals?.total_paid || 0);
      const advReq = Number(data?.totals?.advance_required || 0);
      setMessage(`Advance of ${fmt(amount)} recorded.${paidNow >= advReq ? ' Booking CONFIRMED!' : ''}`);
      setAdvanceForm({ amount: '', payment_mode: 'CASH', gateway_ref: '', receipt_no: '' });
      await Promise.all([loadBookings(), loadPayments(selectedBookingId)]);
    } catch (err) {
      setError(err?.message || 'Payment failed');
    } finally {
      setAdvanceBusy(false);
    }
  };

  const adjustAdvance = async (invoiceId) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/banquet/invoices/${invoiceId}/adjust-advance`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ adjust_amount: adjustAmount ? Number(adjustAmount) : null })
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to adjust advance');
      setMessage(`Advance adjusted: ${Number(data?.adjust_amount || 0).toFixed(2)}`);
      setAdjustAmount('');
      await Promise.all([loadBookings(), loadInvoices(selectedBookingId)]);
    } catch (err) {
      setError(err?.message || 'Failed to adjust advance');
    }
  };

  const selectedTax = useMemo(
    () => taxOptions.find((t) => String(t.id) === String(invoiceForm.tax_id)) || null,
    [taxOptions, invoiceForm.tax_id]
  );
  const itemsSubtotal = useMemo(
    () => (invoiceForm.items || []).reduce((sum, row) => {
      const name = String(row.item_name || '').trim();
      if (!name) return sum;
      const qty = Number(row.qty || 1);
      const unitPrice = Number(row.unit_price || 0);
      const lineAmountRaw = row.line_amount !== '' && row.line_amount != null ? Number(row.line_amount) : (qty * unitPrice);
      return sum + Math.max(Number.isFinite(lineAmountRaw) ? lineAmountRaw : 0, 0);
    }, 0),
    [invoiceForm.items]
  );
  const invoiceBase = Number(itemsSubtotal || invoiceForm.invoice_amount || 0);
  const previewCgst = selectedTax ? (invoiceBase * Number(selectedTax.cgst_percent || 0)) / 100 : 0;
  const previewSgst = selectedTax ? (invoiceBase * Number(selectedTax.sgst_percent || 0)) / 100 : 0;
  const previewIgst = selectedTax ? (invoiceBase * Number(selectedTax.igst_percent || 0)) / 100 : 0;
  const previewTax = previewCgst + previewSgst + previewIgst;
  const previewTotal = invoiceBase + previewTax;

  const updateItemRow = (index, patch) => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((row, i) => (i === index ? { ...row, ...patch } : row))
    }));
  };
  const addItemRow = () => setInvoiceForm((prev) => ({ ...prev, items: [...(prev.items || []), emptyItem()] }));
  const removeItemRow = (index) => {
    setInvoiceForm((prev) => {
      const next = (prev.items || []).filter((_, i) => i !== index);
      return { ...prev, items: next.length ? next : [emptyItem()] };
    });
  };
  const toggleInvoiceItems = async (invoiceId) => {
    if (String(openInvoiceItemsId) === String(invoiceId)) { setOpenInvoiceItemsId(''); return; }
    setOpenInvoiceItemsId(String(invoiceId));
    if (!invoiceItemsById[invoiceId]) {
      try { await loadInvoiceItems(invoiceId); } catch (err) { setError(err?.message || 'Failed to load items'); }
    }
  };

  const statusBadge = (status) => {
    const colors = { TENTATIVE: { bg: '#fef3c7', color: '#92400e' }, CONFIRMED: { bg: '#d1fae5', color: '#065f46' }, CANCELLED: { bg: '#fee2e2', color: '#991b1b' }, COMPLETED: { bg: '#dbeafe', color: '#1e40af' } };
    const s = colors[status] || { bg: '#f3f4f6', color: '#374151' };
    return <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, background: s.bg, color: s.color }}>{status}</span>;
  };

  const bk = selectedBooking;
  const totalPaid = Number(bk?.total_paid || 0);
  const totalValue = Number(bk?.total_value || 0);
  const advanceRequired = Number(bk?.advance_required || 0);
  const balanceDue = Math.max(totalValue - totalPaid, 0);
  const advancePending = Math.max(advanceRequired - totalPaid, 0);
  const bookingStatus = String(bk?.booking_status || '').toUpperCase();
  const isEventPast = bk?.event_end_date && new Date(bk.event_end_date) < new Date(new Date().toISOString().slice(0, 10));

  const S = {
    section: { border: '1px solid #e2e8f0', borderRadius: 8, padding: '1rem', marginBottom: '1rem', background: '#fff' },
    sectionTitle: { margin: '0 0 0.6rem', fontSize: '0.95rem', fontWeight: 700, color: '#1a365d' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' },
    label: { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '2px' },
    input: { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' },
    select: { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', background: '#fff', boxSizing: 'border-box' },
    btn: { padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
    btnPrimary: { background: '#1a365d', color: '#fff' },
    btnSuccess: { background: '#16a34a', color: '#fff' },
    btnWarn: { background: '#f59e0b', color: '#fff' },
    btnOutline: { background: '#fff', color: '#1a365d', border: '1.5px solid #1a365d' },
    tab: (active) => ({ padding: '0.5rem 1.2rem', cursor: 'pointer', borderRadius: '6px 6px 0 0', fontSize: '0.85rem', fontWeight: 600, background: active ? '#1a365d' : '#e2e8f0', color: active ? '#fff' : '#475569', border: 'none' }),
    finCard: (color) => ({ textAlign: 'center', flex: 1, minWidth: 110, padding: '0.5rem', borderRadius: 6, background: '#fff', border: `1.5px solid ${color}20` }),
    finLabel: { fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' },
    finValue: (color) => ({ fontSize: '1.1rem', fontWeight: 700, color, marginTop: '2px' }),
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' },
    th: { textAlign: 'left', padding: '6px 8px', background: '#f1f5f9', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' },
    thR: { textAlign: 'right', padding: '6px 8px', background: '#f1f5f9', fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' },
    td: { padding: '6px 8px', borderBottom: '1px solid #e2e8f0' },
    tdR: { padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontFamily: 'monospace' }
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)', marginBottom: '0.5rem' }}>Banquet - Bookings & Invoices</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      {/* Booking Selector */}
      <div style={{ margin: '0.5rem 0', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={selectedBookingId} onChange={(e) => setSelectedBookingId(e.target.value)} style={{ minWidth: isMobile ? 0 : 350, ...S.select }}>
          <option value="">Select booking</option>
          {bookings.map((b) => (
            <option key={b.booking_id} value={b.booking_id}>
              #{b.booking_id} - {b.event_name || 'Event'} ({b.booking_status}) — {b.customer_name || 'N/A'}
            </option>
          ))}
        </select>
        <button onClick={reload} style={{ ...S.btn, ...S.btnOutline }}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      {/* ═══ Booking Summary ═══ */}
      {bk && (
        <>
          <div style={{ ...S.section, background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <h3 style={{ margin: 0, color: '#1a365d' }}>Booking #{bk.booking_id} — {bk.event_name || 'Banquet Event'}</h3>
              {statusBadge(bk.booking_status)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem 1.2rem', fontSize: '0.88rem' }}>
              <div><strong>Customer:</strong> {bk.customer_name || '—'}</div>
              <div><strong>Phone:</strong> {bk.customer_phone || '—'}</div>
              <div><strong>Email:</strong> {bk.customer_email || '—'}</div>
              <div><strong>Event Date:</strong> {bk.event_start_date}{bk.event_end_date && bk.event_end_date !== bk.event_start_date ? ` to ${bk.event_end_date}` : ''}</div>
              <div><strong>Slot:</strong> {bk.slot_code || '—'}{bk.event_start_time ? ` (${String(bk.event_start_time).slice(0,5)} – ${String(bk.event_end_time || '').slice(0,5)})` : ''}</div>
              <div><strong>Pax:</strong> {bk.expected_pax || bk.layout_pax || '—'}</div>
              <div><strong>Hall:</strong> {bk.hall_name || '—'}</div>
              <div><strong>Layout:</strong> {bk.layout_name || '—'}{bk.seating_type ? ` (${bk.seating_type})` : ''}</div>
              {bk.quotation_no && <div><strong>Quotation:</strong> {bk.quotation_no}</div>}
              {bk.lead_no && <div><strong>Lead:</strong> {bk.lead_no}{bk.lead_event_type ? ` — ${bk.lead_event_type}` : ''}{bk.lead_source ? ` (${bk.lead_source})` : ''}</div>}
            </div>

            {/* Financial Cards */}
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              <div style={S.finCard('#1e293b')}>
                <div style={S.finLabel}>Total Value</div>
                <div style={S.finValue('#1e293b')}>{fmt(totalValue)}</div>
                {bk.quotation_tax_percent > 0 && <div style={{ fontSize: '0.68rem', color: '#64748b' }}>incl. {bk.quotation_tax_percent}% GST</div>}
              </div>
              <div style={S.finCard('#f59e0b')}>
                <div style={S.finLabel}>Advance Required</div>
                <div style={S.finValue('#f59e0b')}>{fmt(advanceRequired)}</div>
                {advancePending > 0 && <div style={{ fontSize: '0.68rem', color: '#dc2626', fontWeight: 600 }}>Pending: {fmt(advancePending)}</div>}
              </div>
              <div style={S.finCard('#22c55e')}>
                <div style={S.finLabel}>Total Paid</div>
                <div style={S.finValue('#22c55e')}>{fmt(totalPaid)}</div>
                {payments.length > 0 && <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{payments.length} payment(s)</div>}
              </div>
              <div style={S.finCard(balanceDue > 0 ? '#ef4444' : '#22c55e')}>
                <div style={S.finLabel}>Balance Due</div>
                <div style={S.finValue(balanceDue > 0 ? '#ef4444' : '#22c55e')}>{fmt(balanceDue)}</div>
              </div>
            </div>

            {/* Status-driven action hint */}
            {bookingStatus === 'TENTATIVE' && advancePending > 0 && (
              <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: '0.82rem', color: '#92400e', fontWeight: 600 }}>
                Collect advance of {fmt(advancePending)} to confirm this booking.
              </div>
            )}
            {bookingStatus === 'CONFIRMED' && isEventPast && (
              <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6, fontSize: '0.82rem', color: '#1e40af', fontWeight: 600 }}>
                Event date has passed. Generate the final invoice based on actuals.
              </div>
            )}
          </div>

          {/* Quotation Items */}
          {quotationItems.length > 0 && (
            <div style={{ ...S.section, paddingTop: '0.6rem', paddingBottom: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>Quotation Items</h4>
                {bk.quotation_subtotal > 0 && (
                  <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    Subtotal: {fmt(bk.quotation_subtotal)}
                    {Number(bk.quotation_discount || 0) > 0 ? ` | Disc: -${fmt(bk.quotation_discount)}` : ''}
                    {' | '}Taxable: {fmt(bk.quotation_taxable)} + Tax ({bk.quotation_tax_percent}%): {fmt(bk.quotation_tax_amount)} = <strong>{fmt(bk.quotation_grand_total)}</strong>
                  </span>
                )}
              </div>
              <table style={S.table}>
                <thead>
                  <tr><th style={S.th}>#</th><th style={S.th}>Type</th><th style={S.th}>Description</th><th style={S.thR}>Amount</th></tr>
                </thead>
                <tbody>
                  {quotationItems.map((qi, idx) => (
                    <tr key={qi.item_id}>
                      <td style={S.td}>{idx + 1}</td>
                      <td style={S.td}>{qi.item_type || '—'}</td>
                      <td style={S.td}>{qi.description || '—'}</td>
                      <td style={S.tdR}>{fmt(qi.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ Tabbed Sections ═══ */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: 0, borderBottom: '2px solid #e2e8f0' }}>
            <button style={S.tab(activeSection === 'advance')} onClick={() => setActiveSection('advance')}>
              Advance Payments {payments.length > 0 ? `(${payments.length})` : ''}
            </button>
            <button style={S.tab(activeSection === 'invoice')} onClick={() => setActiveSection('invoice')}>
              Generate Invoice {invoices.length > 0 ? `(${invoices.length})` : ''}
            </button>
            <button style={S.tab(activeSection === 'history')} onClick={() => setActiveSection('history')}>
              Invoice History
            </button>
          </div>

          {/* ── ADVANCE PAYMENTS ── */}
          {activeSection === 'advance' && (
            <div style={S.section}>
              <h4 style={S.sectionTitle}>Record Advance Payment</h4>
              <div style={{ ...S.grid, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: '0.6rem' }}>
                <div>
                  <div style={S.label}>Amount *</div>
                  <input type="number" step="0.01" min="0" value={advanceForm.amount} onChange={(e) => setAdvanceForm((p) => ({ ...p, amount: e.target.value }))} placeholder={advancePending > 0 ? `Pending: ${fmt(advancePending)}` : '0.00'} style={S.input} />
                </div>
                <div>
                  <div style={S.label}>Payment Mode *</div>
                  <select value={advanceForm.payment_mode} onChange={(e) => setAdvanceForm((p) => ({ ...p, payment_mode: e.target.value }))} style={S.select}>
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
                <div>
                  <div style={S.label}>Reference No</div>
                  <input value={advanceForm.gateway_ref} onChange={(e) => setAdvanceForm((p) => ({ ...p, gateway_ref: e.target.value }))} placeholder="Txn/Cheque No" style={S.input} />
                </div>
                <div>
                  <div style={S.label}>Receipt No</div>
                  <input value={advanceForm.receipt_no} onChange={(e) => setAdvanceForm((p) => ({ ...p, receipt_no: e.target.value }))} placeholder="Receipt #" style={S.input} />
                </div>
              </div>
              <button onClick={recordAdvance} disabled={advanceBusy} style={{ ...S.btn, ...S.btnSuccess }}>
                {advanceBusy ? 'Processing...' : 'Record Advance Payment'}
              </button>

              {/* Payment History */}
              {payments.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ ...S.sectionTitle, fontSize: '0.85rem' }}>Payment History</h4>
                  <table style={S.table}>
                    <thead>
                      <tr><th style={S.th}>Date</th><th style={S.th}>Type</th><th style={S.th}>Mode</th><th style={S.thR}>Amount</th><th style={S.th}>Reference</th><th style={S.th}>Receipt</th></tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.payment_id}>
                          <td style={S.td}>{String(p.paid_at || '').slice(0, 10)}</td>
                          <td style={S.td}>{p.payment_type}</td>
                          <td style={S.td}>{p.payment_mode}</td>
                          <td style={S.tdR}>{fmt(p.amount)}</td>
                          <td style={S.td}>{p.gateway_ref || '—'}</td>
                          <td style={S.td}>{p.receipt_no || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── GENERATE INVOICE ── */}
          {activeSection === 'invoice' && (
            <div style={S.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                <h4 style={S.sectionTitle}>Generate Tax Invoice</h4>
                {quotationItems.length > 0 && (
                  <button onClick={copyQuotationToInvoice} style={{ ...S.btn, ...S.btnWarn }}>
                    Load Quotation Items
                  </button>
                )}
              </div>

              {/* Invoice Header Fields */}
              <div style={{ ...S.grid, marginBottom: '0.75rem' }}>
                <div>
                  <div style={S.label}>Invoice Date</div>
                  <input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} style={S.input} />
                </div>
                <div>
                  <div style={S.label}>Customer GSTIN (optional)</div>
                  <input placeholder="15-digit GSTIN" value={invoiceForm.gstin_no} onChange={(e) => setInvoiceForm({ ...invoiceForm, gstin_no: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15) })} style={{ ...S.input, fontFamily: 'monospace' }} maxLength={15} />
                </div>
                <div>
                  <div style={S.label}>Tax Profile</div>
                  <select value={invoiceForm.tax_id} onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_id: e.target.value })} style={S.select}>
                    <option value="">No Tax</option>
                    {taxOptions.map((t) => (
                      <option key={t.id} value={t.id}>{t.tax_name || 'Tax'} [{String(t.tax_regime || 'NONE').toUpperCase()}] ({Number(t.tax_percent || 0).toFixed(2)}%)</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tax Preview */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: '0.85rem' }}>
                <div><strong>Subtotal:</strong> {fmt(invoiceBase)}</div>
                {selectedTax && <div>CGST ({selectedTax.cgst_percent}%): {fmt(previewCgst)}</div>}
                {selectedTax && <div>SGST ({selectedTax.sgst_percent}%): {fmt(previewSgst)}</div>}
                {selectedTax && Number(selectedTax.igst_percent) > 0 && <div>IGST ({selectedTax.igst_percent}%): {fmt(previewIgst)}</div>}
                <div><strong>Tax:</strong> {fmt(previewTax)}</div>
                <div style={{ fontWeight: 700, color: '#1a365d' }}>Total: {fmt(previewTotal)}</div>
              </div>

              {/* Invoice Items */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>Invoice Line Items</h4>
                <button onClick={addItemRow} style={{ ...S.btn, ...S.btnOutline, padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}>+ Add Item</button>
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: '0.75rem' }}>
                <table style={S.table}>
                  <thead>
                    <tr><th style={S.th}>Item Name</th><th style={S.th}>Description</th><th style={S.thR}>Qty</th><th style={S.thR}>Unit Price</th><th style={S.thR}>Amount</th><th style={S.th}>Action</th></tr>
                  </thead>
                  <tbody>
                    {(invoiceForm.items || []).map((row, idx) => {
                      const qty = Number(row.qty || 1);
                      const rate = Number(row.unit_price || 0);
                      const amount = row.line_amount !== '' && row.line_amount != null ? Number(row.line_amount) : (qty * rate);
                      return (
                        <tr key={`item-${idx}`}>
                          <td style={S.td}><input value={row.item_name} onChange={(e) => updateItemRow(idx, { item_name: e.target.value })} placeholder="Service / Item" style={{ ...S.input, minWidth: 140 }} /></td>
                          <td style={S.td}><input value={row.description} onChange={(e) => updateItemRow(idx, { description: e.target.value })} placeholder="Optional" style={{ ...S.input, minWidth: 100 }} /></td>
                          <td style={S.td}><input type="number" step="0.001" min="0" value={row.qty} onChange={(e) => updateItemRow(idx, { qty: e.target.value })} style={{ ...S.input, width: 70, textAlign: 'right' }} /></td>
                          <td style={S.td}><input type="number" step="0.01" min="0" value={row.unit_price} onChange={(e) => updateItemRow(idx, { unit_price: e.target.value })} style={{ ...S.input, width: 100, textAlign: 'right' }} /></td>
                          <td style={S.td}><input type="number" step="0.01" min="0" value={row.line_amount} onChange={(e) => updateItemRow(idx, { line_amount: e.target.value })} placeholder={Number.isFinite(amount) ? amount.toFixed(2) : '0.00'} style={{ ...S.input, width: 100, textAlign: 'right' }} /></td>
                          <td style={S.td}><button onClick={() => removeItemRow(idx)} style={{ ...S.btn, background: '#ef4444', color: '#fff', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}>Remove</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button onClick={createInvoice} style={{ ...S.btn, ...S.btnPrimary, padding: '0.6rem 1.5rem' }}>Post Invoice</button>
            </div>
          )}

          {/* ── INVOICE HISTORY ── */}
          {activeSection === 'history' && (
            <div style={S.section}>
              <h4 style={S.sectionTitle}>Posted Invoices</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Invoice</th><th style={S.th}>Date</th><th style={S.th}>GSTIN</th>
                      <th style={S.thR}>Subtotal</th><th style={S.th}>Tax</th><th style={S.thR}>Tax Amt</th>
                      <th style={S.thR}>Total</th><th style={S.thR}>Adv. Adjusted</th><th style={S.thR}>Balance</th>
                      <th style={S.th}>Items</th><th style={S.th}>Adjust</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <Fragment key={inv.invoice_id}>
                        <tr>
                          <td style={{ ...S.td, fontWeight: 600 }}>{inv.invoice_no}</td>
                          <td style={S.td}>{String(inv.invoice_date || '').slice(0, 10)}</td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '0.75rem' }}>{inv.gstin_no || '—'}</td>
                          <td style={S.tdR}>{fmt(inv.invoice_amount)}</td>
                          <td style={S.td}>{inv.tax_name || 'No Tax'}</td>
                          <td style={S.tdR}>{fmt(inv.tax_amount)}</td>
                          <td style={{ ...S.tdR, fontWeight: 600 }}>{fmt(inv.total_amount)}</td>
                          <td style={{ ...S.tdR, color: '#22c55e' }}>{fmt(inv.adjusted_advance_amount)}</td>
                          <td style={{ ...S.tdR, color: Number(inv.balance_amount || 0) > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{fmt(inv.balance_amount)}</td>
                          <td style={S.td}>
                            <button onClick={() => toggleInvoiceItems(inv.invoice_id)} style={{ ...S.btn, ...S.btnOutline, padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}>
                              {String(openInvoiceItemsId) === String(inv.invoice_id) ? 'Hide' : `View (${inv.item_count || 0})`}
                            </button>
                          </td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <input placeholder="Amt" type="number" step="0.01" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} style={{ ...S.input, width: 80 }} />
                              <button onClick={() => adjustAdvance(inv.invoice_id)} style={{ ...S.btn, ...S.btnWarn, padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}>Adjust</button>
                            </div>
                          </td>
                        </tr>
                        {String(openInvoiceItemsId) === String(inv.invoice_id) && (
                          <tr>
                            <td colSpan={11} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '0.5rem' }}>
                              <table style={S.table}>
                                <thead><tr><th style={S.th}>#</th><th style={S.th}>Item</th><th style={S.th}>Description</th><th style={S.thR}>Qty</th><th style={S.thR}>Rate</th><th style={S.thR}>Amount</th></tr></thead>
                                <tbody>
                                  {(invoiceItemsById[inv.invoice_id] || []).map((item) => (
                                    <tr key={item.invoice_item_id}>
                                      <td style={S.td}>{item.line_no}</td>
                                      <td style={S.td}>{item.item_name}</td>
                                      <td style={S.td}>{item.description || '—'}</td>
                                      <td style={S.tdR}>{Number(item.qty || 0).toFixed(3)}</td>
                                      <td style={S.tdR}>{fmt(item.unit_price)}</td>
                                      <td style={S.tdR}>{fmt(item.line_amount)}</td>
                                    </tr>
                                  ))}
                                  {!invoiceItemsById[inv.invoice_id]?.length && <tr><td colSpan={6} style={S.td}>No items found.</td></tr>}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                    {!invoices.length && <tr><td colSpan={11} style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: '1.5rem' }}>No invoices posted yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BanquetBooking;
